import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import { Context as AuthContext } from "../../context/AuthContext";
import { Toast } from "primereact/toast";
import { Modal } from "bootstrap";
import { AiFillStar } from "react-icons/ai";
import "../../css/facilityDetail.css";
import FacilityDetailView from "./facilityDetail/FacilityDetailView";
import { formatCategoryLabel } from "./facilityDetail/facilityDetailConstants";
import { axiosAuth } from "../../utils/axiosInstance";
import { Default } from "../layouts/default";
import Loader from "../common/loader";
import NotFound from "./page-not-found";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { PAYMENT_METHODS } from "../../constants/paymentMethods";

// Environment variables
const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const IMG_URL = process.env.REACT_APP_IMG_URL;

// StarRating Component
const StarRating = ({ rating }) => {
  return (
    <div className="flex">
      {[...Array(5)].map((_, i) => (
        <AiFillStar key={i} color={i < rating ? "#FFD700" : "#ccc"} size={20} />
      ))}
    </div>
  );
};

// Updated generateTimeSlots function to produce "HH:MM-HH:MM" format
const generateTimeSlots = (court) => {
  const timeslots = [];
  const duration = parseInt(court.duration);
  const addSlots = (startTime, endTime) => {
    if (!startTime || !endTime) return;
    let start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    while (start < end) {
      const endSlot = new Date(start.getTime() + duration * 60000);
      const formatTime = (date) =>
        `${date.getHours().toString().padStart(2, "0")}:${date
          .getMinutes()
          .toString()
          .padStart(2, "0")}`;
      timeslots.push(`${formatTime(start)}-${formatTime(endSlot)}`);
      start = endSlot;
    }
  };
  addSlots(court.day_start_time, court.day_end_time);
  addSlots(court.night_start_time, court.night_end_time);
  return timeslots;
};

const Single = () => {
  const { state } = useContext(AuthContext);
  const { slug } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [facility, setFacility] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [courtId, setCourtId] = useState(null);
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(Cookies.get("USER_TOKEN"));
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [verifingPayment, setVerifingPayment] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [allAmenities, setAllAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(true);
  const [isPaymentCancelled, setIsPaymentCancelled] = useState(false);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [value, onChange] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [servicedata, setServicedata] = useState(null);
  const [nearbyFacilities, setNearbyFacilities] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(true);
  const [nearbyError, setNearbyError] = useState(null);
  const [userLocation, setUserLocation] = useState({ lat: null, lng: null });
  const [locationError, setLocationError] = useState(null);
  const [slotsPrice, setSlotsPrice] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTimeSlots, setCurrentTimeSlots] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

// Refresh confirmation popup
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();

      // Required for Chrome
      event.returnValue = "";

      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const [activeTab, setActiveTab] = useState("about");

  // Fetch Amenities
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await fetch(`${BASE_URL}/get-all-amenities`);
        const data = await response.json();
        setAllAmenities(data?.amenities || []);
      } catch (error) {
        console.error("Error fetching amenities:", error);
      } finally {
        setAmenitiesLoading(false);
      }
    };
    fetchAmenities();
  }, []);

  // Fetch Facility by Slug
  useEffect(() => {
    const getFacilitySlug = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/get-facility-by-slug/${slug}`,
        );

        if (response.data.facility) {
          const facilityData = response.data.facility;

          // Parse amenities if it's a string
          if (typeof facilityData.amenities === "string") {
            try {
              facilityData.amenities = JSON.parse(facilityData.amenities);
            } catch (e) {
              facilityData.amenities = [];
            }
          }

          // Handle images
          let allImages = [];
          try {
            const parsedImages = JSON.parse(facilityData.images || "[]");
            if (Array.isArray(parsedImages)) {
              allImages = parsedImages.map((img) => img.replace(/\\/g, "/"));
            }
          } catch (e) {
            console.warn("Images could not be parsed:", e);
          }
          if (facilityData.featured_image) {
            allImages.unshift(facilityData.featured_image.replace(/\\/g, "/"));
          }

          setImages(allImages);
          setFacility(facilityData);

          // Set reviews
          if (Array.isArray(facilityData.reviews)) {
            setReviews(facilityData.reviews);
          } else {
            setReviews([]);
          }
        }
      } catch (err) {
        console.error("Error fetching facility:", err);
      } finally {
        setIsLoading(false);
        setReviewsLoading(false);
      }
    };

    setReviewsLoading(true);
    getFacilitySlug();
  }, [slug]);

  // Fetch User Profile Data
  useEffect(() => {
    if (state.userData) {
      const fetchUserProfileData = async () => {
        try {
          setProfileLoading(true);
          const response = await axiosAuth.get("get-user-details");
          if (response.status === 200 && response.data?.user) {
            const user = response.data.user;
            setUserProfileData({
              name: user.name || "",
              email: user.email || "",
              contact: user.contact || user.phone || "",
              address: user.address || "",
            });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        } finally {
          setProfileLoading(false);
        }
      };
      fetchUserProfileData();
    }
  }, [state.userData]);

  // Geolocation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLocationError(
            "Unable to retrieve your location. Please enable location services or try again.",
          );
        },
      );
    } else {
      setLocationError("Geolocation is not supported by your browser.");
    }
  }, []);

  // Utility function to calculate distance (Haversine formula)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return isNaN(distance) ? Infinity : distance;
  };

  // Fetch Nearby Facilities
  useEffect(() => {
    if (userLocation.lat && userLocation.lng && facility?.id) {
      const fetchAllFacilities = async () => {
        try {
          setNearbyLoading(true);
          setNearbyError(null);
          const response = await axiosAuth.get(`${BASE_URL}/get-all-facility`);

          // Handle the response structure
          let allFacilities = [];
          if (response.data.facility && Array.isArray(response.data.facility)) {
            allFacilities = response.data.facility;
          } else if (Array.isArray(response.data)) {
            allFacilities = response.data;
          } else if (
            response.data.facilities &&
            Array.isArray(response.data.facilities)
          ) {
            allFacilities = response.data.facilities;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            allFacilities = response.data.data;
          } else if (
            response.data.results &&
            Array.isArray(response.data.results)
          ) {
            allFacilities = response.data.results;
          } else if (
            response.data.items &&
            Array.isArray(response.data.items)
          ) {
            allFacilities = response.data.items;
          } else {
            setNearbyError(
              "Invalid data format from API. Expected an array of facilities.",
            );
            setNearbyLoading(false);
            return;
          }

          if (allFacilities.length === 0) {
            setNearbyError("No facilities found in the API response.");
            setNearbyLoading(false);
            return;
          }

          // Filter out current facility, add distance, sort by distance, take top 3
          const filtered = allFacilities
            .filter((f) => {
              const isDifferent = f.id !== facility.id;
              const hasValidCoords =
                f.lat &&
                f.lng &&
                !isNaN(parseFloat(f.lat)) &&
                !isNaN(parseFloat(f.lng));
              return isDifferent && hasValidCoords;
            })
            .map((f) => {
              const distance = getDistance(
                parseFloat(userLocation.lat),
                parseFloat(userLocation.lng),
                parseFloat(f.lat),
                parseFloat(f.lng),
              );
              return {
                ...f,
                distance,
                minimum_amount: f.minimum_amount || "N/A",
                duration: f.duration || "N/A",
              };
            })
            .filter((f) => f.distance !== Infinity)
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 3);

          setNearbyFacilities(filtered);

          if (filtered.length === 0) {
            setNearbyError(
              "No nearby facilities found within a reasonable distance.",
            );
          }
        } catch (err) {
          setNearbyError("Failed to load nearby facilities: " + err.message);
        } finally {
          setNearbyLoading(false);
        }
      };

      fetchAllFacilities();
    } else {
      if (locationError) {
        setNearbyLoading(false);
        setNearbyError(locationError);
      }
    }
  }, [userLocation, facility?.id]);

  // Fetch Court Pricing
  useEffect(() => {
    if (
      facility?.services &&
      Array.isArray(facility?.services) &&
      facility?.services?.length > 0
    ) {
      const allCourts = facility.services.flatMap((service) => service.court);
      const minCourt = allCourts.reduce((min, court) => {
        const price = Number(court?.day_slot_price);
        return price < Number(min?.day_slot_price) ? court : min;
      }, allCourts[0]);

      setSlotsPrice(Number(minCourt?.day_slot_price) || 0);
      setDuration(minCourt?.duration || null);
    }
  }, [facility]);

  // Utility Functions
  const checkServiceDisabled = (id) => {
    const service = facility?.services?.find((item) => item.id === id);
    if (!service || !service.upcoming_holiday) return null;
    const dateArray = service.upcoming_holiday
      .split(",")
      .map((date) => date.trim());
    const dateToCheck = new Date(value);
    const formattedDateToCheck = `${dateToCheck
      .getDate()
      .toString()
      .padStart(2, "0")}-${(dateToCheck.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${dateToCheck.getFullYear()}`;
    return dateArray.includes(formattedDateToCheck);
  };

  const handleSlotClick = (slot) => {
    const isSelected = selectedSlots.includes(slot);
    let updatedSlots = [];

    if (isSelected) {
      updatedSlots = selectedSlots.filter((s) => s !== slot);
    } else {
      if (selectedSlots.length >= 3) {
        toast.current.show({
          severity: "error",
          summary: "Selection Limit",
          detail: "You can select maximum 3 slots only.",
          life: 2000,
        });
        return;
      }

      updatedSlots = [...selectedSlots, slot];
    }

    setSelectedSlots(updatedSlots);

    localStorage.setItem("selectedSlots", JSON.stringify(updatedSlots));
  };

  const getSlotPrice = (slot, court) => {
    const [startTime] = slot.split("-");
    const hour = parseInt(startTime.split(":")[0]);
    const nightStart = parseInt(court.night_start_time?.split(":")[0]);
    return hour >= nightStart ? court.night_slot_price : court.day_slot_price;
  };

  const handleCourtChange = async (court) => {
    setCourtId(court.id);
    setSelectedSlots([]);
    setBookedSlots([]);
    const bookingDate = new Date(value);
    const formattedDate = `${bookingDate.getFullYear()}-${(
      bookingDate.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${bookingDate.getDate().toString().padStart(2, "0")}`;
    const formData = {
      court_id: court.id,
      facility_service_id: selectedService,
      date: formattedDate,
    };
    // Generate time slots
    setCurrentTimeSlots(generateTimeSlots(court));
    // Fetch booked slots
    getSlotsOfCourt.mutate(formData);
  };

  const clearAllSlots = () => setSelectedSlots([]);

  const removeSlot = (slotToRemove) => {
    setSelectedSlots(selectedSlots.filter((slot) => slot !== slotToRemove));
  };

  const calculateTotalPrice = () => {
    const selectedCourt = servicedata
      ?.find((s) => s.id === selectedService)
      ?.court.find((c) => c.id === courtId);
    if (!selectedCourt) return 0;
    return selectedSlots.reduce(
      (acc, slot) => acc + parseInt(getSlotPrice(slot, selectedCourt)),
      0,
    );
  };

  const resetForm = () => {
    setSelectedService(null);
    setCourtId(null);
    setSelectedSlots([]);
    setBookedSlots([]);
  };

  const clearBookingSelections = () => {
    onChange(null);
    setSelectedService(null);
    setCourtId(null);
    setSelectedSlots([]);
    setBookedSlots([]);
    setCurrentTimeSlots([]);
    setShowBookingSummary(false);
  };

  const handleToBookNow = () => {
    if (selectedSlots.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select at least one slot.",
        life: 2000,
      });
      return;
    }
    setShowBookingSummary(true);
    setVerifingPayment(true);
  };

  // Share Functionality
  const handleShare = async (platform) => {
    const shareData = {
      title: facility?.official_name || "BookVenue Facility",
      text: `Check out this amazing sports facility: ${
        facility?.official_name || "BookVenue Facility"
      }! ${facility?.description || ""}`,
      url: window.location.href,
    };

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareData.url,
      )}&t=${encodeURIComponent(shareData.text)}`,
      instagram: `https://www.instagram.com/`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareData.url,
      )}&text=${encodeURIComponent(shareData.text)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
        shareData.url,
      )}&description=${encodeURIComponent(shareData.text)}&media=${
        images[0] ? encodeURIComponent(`${IMG_URL}${images[0]}`) : ""
      }`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareData.url,
      )}`,
    };

    try {
      if (platform === "generic" && navigator.share) {
        await navigator.share(shareData);
      } else if (platform in shareUrls) {
        window.open(shareUrls[platform], "_blank", "noopener,noreferrer");
        toast.current.show({
          severity: "success",
          summary: "Shared",
          detail: `Opened ${platform} for sharing!`,
          life: 2000,
        });
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.current.show({
          severity: "success",
          summary: "Link Copied",
          detail: "Link copied to clipboard!",
          life: 2000,
        });
      }
    } catch (error) {
      console.error(`Error sharing on ${platform}:`, error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to share content.",
        life: 3000,
      });
    }
  };

  // API Mutations
  const getSlotsOfCourt = useMutation(
    (formData) =>
      fetch(`${BASE_URL}/get-slots-of-court`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }).then((response) => {
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
      }),
    {
      onSuccess: (data, variables) => {
        if (data && Array.isArray(data.slots)) {
          // Get the court from servicedata based on variables.court_id
          const selectedCourt = servicedata
            ?.find((s) => s.id === variables.facility_service_id)
            ?.court.find((c) => c.id === variables.court_id);
          if (!selectedCourt) {
            setBookedSlots([]);
            return;
          }
          // Generate all slots to align indices
          const allSlots = generateTimeSlots(selectedCourt);
          // Filter booked slots that are not in the past
          const bookedIndices = data.slots
            .map((slot, idx) =>
              slot.booked && !isPastSlot(allSlots[idx]) ? idx : -1,
            )
            .filter((idx) => idx !== -1);
          setBookedSlots(bookedIndices);
          if (data.slots.length === 0) {
            toast.current.show({
              severity: "warn",
              summary: "No Slots Available",
              detail: "No slots available for this court and date.",
              life: 3000,
            });
          }
        } else {
          setBookedSlots([]);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Invalid slot data received.",
            life: 3000,
          });
        }
      },
      onError: (error) => {
        setBookedSlots([]);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to fetch slots.",
          life: 3000,
        });
      },
    },
  );

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSlotsbydate = async (selectedDate) => {
    try {
      const date = formatDate(selectedDate);
      const response = await axios.get(
        `${BASE_URL}/get-slot-by-selected-date/${date}/${slug}`,
      );
      setServicedata(response.data.services);
    } catch (error) {
      console.error("Error in getSlotsbydate:", error);
    }
  };

  const onSubmit = async () => {
    try {
      const date = new Date(value);
      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;

      const selectedServiceData = servicedata?.find(
        (item) => item.id === selectedService,
      );
      const selectedCourt = selectedServiceData?.court?.find(
        (item) => item.id === courtId,
      );

      if (!selectedCourt) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Selected court not found.",
          life: 3000,
        });
        return;
      }

      const selectedSlotDetails = selectedSlots.map((slot) => {
        const [startTime, endTime] = slot.split("-");
        const price = Number(getSlotPrice(slot, selectedCourt));
        return {
          start_time: startTime,
          end_time: endTime,
          price,
        };
      });

      const totalPrice = selectedSlotDetails.reduce(
        (sum, slot) => sum + slot.price,
        0,
      );
      const slotTimings = selectedSlotDetails.map((slot, index) => ({
        slot_number: index + 1,
        day_start_time: slot.start_time,
        day_end_time: slot.end_time,
        day_price: slot.price,
      }));

      const requestBody = {
        date: formattedDate,
        type: "sports",
        facility_id: facility.id,
        duration: selectedCourt?.duration,
        total_price: totalPrice,
        court_id: courtId,
        name: userProfileData?.name || "",
        email: userProfileData?.email || "",
        contact: userProfileData?.contact || "",
        address: userProfileData?.address || "",
        selected_slots: selectedSlotDetails,
        slot_timings: slotTimings,
        slot_count: selectedSlots.length,
      };

      const response = await axiosAuth.post(`${BASE_URL}/booking`, requestBody);
      if (response) {
        initiatePayment(response.data);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Booking initiated successfully.",
          life: 2000,
        });
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong. Please try again.",
        life: 3000,
      });
    }
  };

  const initiatePayment = (data) => {
    setVerifingPayment(false);
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: data.order.amount,
      currency: data.order.currency,
      name: "Bookvenue",
      description: "Payment request.",
      image: "https://bookvenue.app/final-bv-logo.png",
      order_id: data.order.id,
      handler: async (response) => {
        try {
          setVerifingPayment(true);
          const apiResponse = await axiosAuth.post(
            `${BASE_URL}/payment-success`,
            {
              order_id: data?.order?.id,
            },
          );

          if (apiResponse.statusText === "OK") {
            setOrderId(apiResponse?.data?.payment?.id);
            const modal = document.getElementById("useThisAddressModalToggle");
            if (modal) {
              Modal.getOrCreateInstance(modal).hide();
            }
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Payment successful!",
              life: 2000,
            });
            setShowSuccessPopup(true);
            setVerifingPayment(false);
            resetForm();
          } else {
            throw new Error("Payment verification failed");
          }
        } catch (error) {
          setVerifingPayment(false);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Payment verification failed!",
            life: 3000,
          });
        }
      },
      notes: { cart: data.order.notes },
      theme: { color: "#269089" },
      modal: {
        ondismiss: () => {
          setVerifingPayment(false);
          setIsPaymentCancelled(true);
          toast.current.show({
            severity: "warn",
            summary: "Payment Cancelled",
            detail: "Payment was cancelled by user",
            life: 2000,
          });
        },
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.on("payment.failed", async () => {
      try {
        await axiosAuth.get(`${BASE_URL}/payment-failure`, {
          params: { order_id: data?.order?.id },
        });
        toast.current.show({
          severity: "error",
          summary: "Payment Failed",
          detail: "Payment failed. Please try again.",
          life: 3000,
        });
      } catch (error) {
        console.error("Error in payment fail API:", error);
      }
      setVerifingPayment(false);
    });
    rzp1.open();
  };

  const isSlotBooked = (index) => {
    return bookedSlots.includes(index);
  };

  const isPastSlot = (slot) => {
    const today = new Date();
    const selectedDate = new Date(value);

    // Only apply check if selected date is today
    if (
      today.getFullYear() === selectedDate.getFullYear() &&
      today.getMonth() === selectedDate.getMonth() &&
      today.getDate() === selectedDate.getDate()
    ) {
      const [startTime] = slot.split("-");
      const startHour = parseInt(startTime.split(":")[0]);
      const startMin = parseInt(startTime.split(":")[1]);

      const slotStart = new Date(today);
      slotStart.setHours(startHour, startMin, 0, 0);

      return today >= slotStart; // Returns true if slot has started or passed
    }

    return false; // Future dates are not considered past
  };

  const bookingStep = !value ? 1 : !selectedService ? 2 : !courtId ? 3 : 4;
  const categoryLabel = formatCategoryLabel(facility?.category);
  const selectedCourtData = servicedata
    ?.find((s) => s.id === selectedService)
    ?.court?.find((c) => c.id === courtId);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // JSX Rendering
  return (
    <>
      <Toast ref={toast} position="bottom-right" />
      {isLoading ? (
        <Loader />
      ) : facility ? (
        <Default>
          <FacilityDetailView
            facility={facility}
            images={images}
            IMG_URL={IMG_URL}
            categoryLabel={categoryLabel}
            reviews={reviews}
            reviewsLoading={reviewsLoading}
            StarRating={StarRating}
            nearbyFacilities={nearbyFacilities}
            nearbyLoading={nearbyLoading}
            nearbyError={nearbyError}
            onRetryLocation={() => {
              setLocationError(null);
              setNearbyError(null);
              setNearbyLoading(true);
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                  setLocationError(null);
                },
                () => {
                  setLocationError(
                    "Unable to retrieve your location. Please enable location services or try again.",
                  );
                },
              );
            }}
            navigate={navigate}
            activeTab={activeTab}
            onTabClick={handleTabClick}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            photoIndex={photoIndex}
            setPhotoIndex={setPhotoIndex}
            handleShare={handleShare}
            bookingStep={bookingStep}
            value={value}
            onDateChange={(newValue) => {
              onChange(newValue);
              setSelectedService(null);
              setCourtId(null);
              setSelectedSlots([]);
              setBookedSlots([]);
              getSlotsbydate(newValue);
            }}
            servicedata={servicedata}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            setSelectedSlots={setSelectedSlots}
            setBookedSlots={setBookedSlots}
            checkServiceDisabled={checkServiceDisabled}
            courtId={courtId}
            selectedCourtData={selectedCourtData}
            handleCourtChange={handleCourtChange}
            setSlotsPrice={setSlotsPrice}
            setDuration={setDuration}
            currentTimeSlots={currentTimeSlots}
            isPastSlot={isPastSlot}
            isSlotBooked={isSlotBooked}
            handleSlotClick={handleSlotClick}
            selectedSlots={selectedSlots}
            getSlotPrice={getSlotPrice}
            calculateTotalPrice={calculateTotalPrice}
            state={state}
            handleToBookNow={handleToBookNow}
            onClearBooking={clearBookingSelections}
          />

            {showBookingSummary && (
              <div
                className="fv-checkout-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="fv-checkout-title"
                onClick={() => {
                  setOrderId(null);
                  setShowBookingSummary(false);
                }}
              >
                <div
                  className="fv-checkout-sheet"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="fv-checkout-sheet__handle" aria-hidden="true" />

                  <header className="fv-checkout-sheet__bar">
                    <span className="fv-checkout-sheet__badge">
                      <i className="fa-solid fa-lock" aria-hidden="true" />
                      Secure
                    </span>
                    <h3 id="fv-checkout-title" className="fv-checkout-sheet__title">
                      Review booking
                    </h3>
                    <button
                      type="button"
                      className="fv-checkout-sheet__close"
                      aria-label="Close"
                      onClick={() => {
                        setOrderId(null);
                        setShowBookingSummary(false);
                      }}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </header>

                  {orderId ? (
                    <div className="fv-checkout-sheet__body fv-checkout-sheet__body--center">
                      <div className="fv-checkout-success">
                        <div className="fv-checkout-success__icon" aria-hidden="true">
                          <i className="fa-solid fa-check" />
                        </div>
                        <h4>Payment successful</h4>
                        <p className="fv-checkout-success__id">
                          Booking ID{" "}
                          <strong>{orderId}</strong>
                        </p>
                        <button
                          type="button"
                          className="fv-btn fv-btn--primary fv-btn--block fv-checkout-pay"
                          onClick={() => {
                            setOrderId(null);
                            setShowBookingSummary(false);
                            navigate("/booking");
                          }}
                        >
                          View my bookings
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="fv-checkout-sheet__body">
                        <section className="fv-checkout-block">
                          <h4 className="fv-checkout-block__title">
                            Booking details
                          </h4>
                          <ul className="fv-checkout-info">
                            <li>
                              <span
                                className="fv-checkout-info__icon"
                                aria-hidden="true"
                              >
                                <i className="fa-regular fa-calendar" />
                              </span>
                              <div className="fv-checkout-info__text">
                                <span>Date</span>
                                <strong>
                                  {value?.toLocaleDateString("en-IN", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </strong>
                              </div>
                            </li>
                            <li>
                              <span
                                className="fv-checkout-info__icon"
                                aria-hidden="true"
                              >
                                <i className="fa-solid fa-person-running" />
                              </span>
                              <div className="fv-checkout-info__text">
                                <span>Activity</span>
                                <strong className="text-capitalize">
                                  {servicedata?.find(
                                    (s) => s.id === selectedService,
                                  )?.name || "—"}
                                </strong>
                              </div>
                            </li>
                            <li>
                              <span
                                className="fv-checkout-info__icon"
                                aria-hidden="true"
                              >
                                <i className="fa-solid fa-table-tennis-paddle-ball" />
                              </span>
                              <div className="fv-checkout-info__text">
                                <span>Court</span>
                                <strong>
                                  {servicedata
                                    ?.find((s) => s.id === selectedService)
                                    ?.court.find((c) => c.id === courtId)
                                    ?.court_name || "—"}
                                </strong>
                              </div>
                            </li>
                          </ul>
                        </section>

                        <section className="fv-checkout-block">
                          <h4 className="fv-checkout-block__title">
                            Time slots
                            <span className="fv-checkout-block__count">
                              {selectedSlots.length}
                            </span>
                          </h4>
                          <ul className="fv-checkout-slots">
                            {selectedSlots.map((slot, idx) => {
                              const selectedCourt = servicedata
                                ?.find((s) => s.id === selectedService)
                                ?.court.find((c) => c.id === courtId);
                              if (!slot || !selectedCourt) return null;
                              const price = getSlotPrice(slot, selectedCourt);
                              return (
                                <li
                                  key={`${slot}-${idx}`}
                                  className="fv-checkout-slot"
                                >
                                  <span className="fv-checkout-slot__time">
                                    <i
                                      className="fa-regular fa-clock"
                                      aria-hidden="true"
                                    />
                                    {slot}
                                  </span>
                                  <strong className="fv-checkout-slot__price">
                                    ₹{price}
                                  </strong>
                                </li>
                              );
                            })}
                          </ul>
                        </section>

                        <section
                          className="fv-checkout-block"
                          aria-label="Accepted payment methods"
                        >
                          <h4 className="fv-checkout-block__title">
                            Pay with
                          </h4>
                          <p className="fv-checkout-block__hint">
                            Available on the next secure payment screen
                          </p>
                          <ul className="fv-checkout-pay-rail">
                            {PAYMENT_METHODS.map((method) => (
                              <li key={method.id}>
                                <span title={method.label}>
                                  <img
                                    src={method.src}
                                    alt={method.label}
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </span>
                              </li>
                            ))}
                          </ul>
                        </section>
                      </div>

                      <footer className="fv-checkout-sheet__dock">
                        <div className="fv-checkout-dock__row">
                          <div className="fv-checkout-dock__total">
                            <span>Total amount</span>
                            <strong>₹{calculateTotalPrice()}</strong>
                          </div>
                          <span className="fv-checkout-dock__slots">
                            {selectedSlots.length} slot
                            {selectedSlots.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="fv-btn fv-btn--primary fv-btn--block fv-checkout-dock__pay"
                          onClick={onSubmit}
                          disabled={
                            !(
                              value &&
                              courtId &&
                              selectedService &&
                              selectedSlots.length > 0
                            )
                          }
                        >
                          <i
                            className="fa-solid fa-lock"
                            aria-hidden="true"
                          />
                          Pay ₹{calculateTotalPrice()}
                        </button>
                        <p className="fv-checkout-dock__trust">
                          <i
                            className="fa-solid fa-shield-halved"
                            aria-hidden="true"
                          />
                          100% secure &amp; encrypted payment
                        </p>
                      </footer>
                    </>
                  )}
                </div>
              </div>
            )}
          {showSuccessPopup && (
            <div className="success-popup-overlay">
              <div className="success-popup-box">
                <div className="success-icon">✓</div>
                <h3>Booking Confirmed!</h3>
                <p>Your booking has been completed successfully.</p>

                <button
                  className="btn btn-success mt-2"
                  onClick={() => {
                    setShowSuccessPopup(false);
                    navigate("/booking"); // optional redirect
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          )}
        </Default>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default Single;
